import { useCallback, useMemo, useRef } from "react";
import { useDashboardSummary, usePeriodSummary, useCalendarSummary } from "@/api/dashboard";
import { useAuth } from "@/context/AuthContext";
import { formatCurrency } from "@/lib/format";
import type { MascotMood } from "./Mascot";

interface Facts {
  consolidatedBalance: number;
  monthIncome: number;
  monthExpense: number;
  monthNet: number;
  savingsRate: number;
  topCategory: { name: string; total: number } | null;
  secondCategory: { name: string; total: number } | null;
  categoryCount: number;
  biggestExpense: { description: string; amount: number; date: string } | null;
  streakDays: number;
  loggedToday: boolean;
  daysSinceLastLog: number | null;
  worstDay: { day: number; amount: number } | null;
}

function dayOfMonth(dateStr: string) {
  return Number(dateStr.slice(8, 10));
}

/** Dia do mês (do mês atual) com maior soma de despesas, a partir do calendário. */
function computeWorstDay(days: Array<{ date: string; expense: number }> | undefined) {
  if (!days || days.length === 0) return null;
  const worst = days.reduce((max, d) => (!max || d.expense > max.expense ? d : max), null as { date: string; expense: number } | null);
  if (!worst || worst.expense <= 0) return null;
  return { day: dayOfMonth(worst.date), amount: worst.expense };
}

/** Estimativa de quantos dias faz desde o último registro, olhando o mês atual e o
 * anterior (o suficiente pra cobrir "virou o mês e não voltou"). É aproximado — baseado
 * na data da transação, não no instante exato em que foi criada — por isso as mensagens
 * que usam esse número são sempre em tom de "mais ou menos". */
function computeDaysSinceLastLog(
  thisMonth: Array<{ date: string }> | undefined,
  prevMonth: Array<{ date: string }> | undefined
) {
  const all = [...(thisMonth ?? []), ...(prevMonth ?? [])];
  if (all.length === 0) return null;
  const lastDate = all.map((d) => d.date).sort().at(-1);
  if (!lastDate) return null;
  const last = new Date(`${lastDate}T00:00:00Z`).getTime();
  const today = new Date();
  const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.round((todayUTC - last) / 86_400_000);
  return diff > 0 ? diff : null;
}

// Mensagens que SÓ aparecem pra quem não é assinante - o mascote puxando conversa pra
// assinar. Sem assinatura a pessoa navega pelo app mas não consegue anotar nada, então o tom
// é sempre "tá tudo aqui, só falta assinar pra começar". Quem já assina não vê nenhuma.
const PREMIUM_PITCH_MESSAGES: string[] = [
  "Dá pra olhar tudo por aqui, mas anotar mesmo só assinando. Bora liberar?",
  "Tô pronto pra acompanhar seus gastos. Só falta você assinar pra eu começar a trabalhar.",
  "R$ 19,90 por mês pra saber pra onde vai cada real.",
  "No plano anual sai por uns R$ 8,33 por mês — 58% mais barato que o mensal.",
  "Assinando, você anota gasto falando comigo: \"gastei 30 no mercado\" e eu registro sozinho.",
  "Calendário financeiro, metas, orçamento e contas a pagar: tudo liberado assim que você assina.",
  "Pode pagar como quiser: PIX, cartão ou boleto.",
  "Você já deu a volta no app. Agora é só assinar e fazer o primeiro lançamento.",
  "Sem assinatura eu só fico olhando. Assina que a gente começa a anotar junto.",
  "Cada dia sem anotar é um dia sem saber pra onde o dinheiro foi. Assina e começa hoje.",
  "Meta de economia com valor e prazo, e o progresso na tela até chegar lá. É só assinar.",
  "Assinou, acabou anúncio. Fica só você, seus números e eu.",
  "Quer cancelar depois? Cancela quando quiser, direto nas Configurações.",
  "O primeiro lançamento é o mais difícil. O segundo já vira hábito. Assina e faz o primeiro.",
  "Contas a pagar com vencimento, tudo num lugar só. Boleto esquecido nunca mais — é só assinar.",
  "Orçamento por categoria: você define o limite e vê na hora quando estourou. Libera assinando.",
  "Tá tudo aqui esperando: dashboard, calendário, metas. Só falta a assinatura pra começar a preencher.",
  "Menos que um lanche por mês pra ter o controle do mês inteiro.",
  "Categorias do seu jeito, não do meu. Assinando você cria as suas.",
  "Psiu: quem anota sabe pra onde o dinheiro vai. E pra anotar aqui, é só assinar.",
];

function buildMessagePool(f: Facts): string[] {
  const pool: string[] = [];

  // --- Categoria que mais pesa / maior gasto do mês ---
  if (f.topCategory) {
    pool.push(`Sua categoria que mais pesou este mês foi ${f.topCategory.name}, com ${formatCurrency(f.topCategory.total)} gastos.`);
    pool.push(`De cada real que saiu esse mês, uma boa parte foi pra ${f.topCategory.name}. Já esperava por isso?`);
    pool.push(`${f.topCategory.name} lidera seus gastos do mês, com ${formatCurrency(f.topCategory.total)} no total.`);
  }
  if (f.secondCategory) {
    pool.push(
      `Depois de ${f.topCategory?.name ?? "sua categoria principal"}, ${f.secondCategory.name} é quem mais pesa, com ${formatCurrency(f.secondCategory.total)}.`
    );
  }
  if (f.categoryCount > 0) {
    pool.push(`Esse mês você já espalhou gastos em ${f.categoryCount} categoria${f.categoryCount > 1 ? "s" : ""} diferente${f.categoryCount > 1 ? "s" : ""}.`);
  }
  if (f.biggestExpense) {
    pool.push(`Sua maior despesa do mês foi "${f.biggestExpense.description}", de ${formatCurrency(f.biggestExpense.amount)}.`);
    pool.push(`Lembra de "${f.biggestExpense.description}"? Foram ${formatCurrency(f.biggestExpense.amount)} só nessa.`);
    pool.push(`O maior gasto anotado esse mês foi ${formatCurrency(f.biggestExpense.amount)}, em "${f.biggestExpense.description}".`);
  }
  if (f.worstDay) {
    pool.push(`O dia ${f.worstDay.day} foi o que mais pesou no seu bolso esse mês: ${formatCurrency(f.worstDay.amount)} só naquele dia.`);
    pool.push(`Fica de olho no dia ${f.worstDay.day} — foi quando você mais gastou esse mês, ${formatCurrency(f.worstDay.amount)} de uma vez.`);
  }

  // --- Sequência de anotações (streak) ---
  if (f.loggedToday && f.streakDays > 0) {
    pool.push(`Você já está anotando há ${f.streakDays} dia${f.streakDays > 1 ? "s" : ""} seguido${f.streakDays > 1 ? "s" : ""}. Não quebra essa corrente!`);
    pool.push(`${f.streakDays} dia${f.streakDays > 1 ? "s" : ""} seguido${f.streakDays > 1 ? "s" : ""} anotando os gastos — essa disciplina é rara, viu?`);
    if (f.streakDays >= 7) pool.push(`Já é ${f.streakDays > 30 ? "mais de um mês" : "mais de uma semana"} anotando todo santo dia. Isso é hábito de verdade.`);
  }
  if (!f.loggedToday && f.streakDays > 0) {
    pool.push(`Você tinha uma sequência de ${f.streakDays} dia${f.streakDays > 1 ? "s" : ""} e ainda não registrou nada hoje. Bora não perder o ritmo?`);
  }
  if (!f.loggedToday && f.daysSinceLastLog !== null && f.daysSinceLastLog >= 1) {
    pool.push(`Já faz uns ${f.daysSinceLastLog} dia${f.daysSinceLastLog > 1 ? "s" : ""} que não aparece um registro seu por aqui. Bora recomeçar agora?`);
  }
  if (!f.loggedToday && f.daysSinceLastLog === null) {
    pool.push("Faz um tempo que você não registra nada por aqui. Bora colocar tudo em dia?");
  }

  // --- Saldo, receita e despesa ---
  pool.push(`Seu saldo consolidado agora é de ${formatCurrency(f.consolidatedBalance)}.`);
  if (f.consolidatedBalance < 0) {
    pool.push(`Seu saldo tá negativo, em ${formatCurrency(f.consolidatedBalance)}. Bora dar uma olhada no que pode ajustar?`);
  }
  if (f.monthIncome > 0 || f.monthExpense > 0) {
    pool.push(`Esse mês você recebeu ${formatCurrency(f.monthIncome)} e gastou ${formatCurrency(f.monthExpense)}.`);
  }
  if (f.monthIncome > 0 && f.monthNet >= 0) {
    pool.push(`Você guardou ${Math.round(f.savingsRate * 100)}% do que ganhou esse mês. Nada mal!`);
  }
  if (f.monthNet < 0) {
    pool.push(`Esse mês os gastos passaram a receita em ${formatCurrency(Math.abs(f.monthNet))}. Vale revisar algo?`);
  }
  if (f.monthIncome > 0 && f.savingsRate >= 0.2) {
    pool.push(`${Math.round(f.savingsRate * 100)}% guardado esse mês — continuar assim é fácil ou é disciplina de verdade?`);
  }

  // --- Dicas gerais de educação financeira (sem indicar produto ou ativo específico) ---
  pool.push("Regra simples: monte uma reserva de emergência antes de pensar em investir o resto.");
  pool.push("Juros compostos trabalham a seu favor quando você guarda, e contra quando você deve. Escolha o lado certo.");
  pool.push("Diversificar não é só sobre investimentos — vale pra fontes de renda também.");
  pool.push("Antes de qualquer aplicação, entenda a taxa, o risco e a liquidez. Sem pressa é sempre melhor.");
  pool.push("Um bom hábito: revisar os gastos fixos a cada 3 meses. Assinatura esquecida é dinheiro escorrendo.");
  pool.push("Guardar 10% do que você ganha todo mês já é um baita começo.");
  pool.push("Categorizar os gastos com carinho hoje facilita muito entender pra onde o dinheiro foi depois.");
  pool.push("Reserva de emergência de verdade fica em algo líquido e seguro, não em promessa de rendimento alto.");
  pool.push("Dívida cara, tipo rotativo de cartão, deveria ser prioridade número um antes de qualquer investimento.");
  pool.push("Investir pouco e sempre costuma valer mais que esperar juntar 'o valor ideal' pra começar.");
  pool.push("Meta sem prazo é só um desejo. Bora colocar uma data nas suas metas?");
  pool.push("Todo real gasto por impulso é um real a menos trabalhando por você lá na frente.");
  pool.push("Anotar até o gasto pequeno é o que separa quem sabe pra onde o dinheiro vai de quem só imagina.");
  pool.push("Rendimento passado não garante nada no futuro — desconfie de quem promete o contrário.");
  pool.push("Orçamento não é prisão, é mapa. Sem ele fica difícil saber se você tá indo na direção certa.");
  pool.push("Automatizar uma transferência pra poupança no dia do pagamento evita a tentação de gastar antes.");
  pool.push("Cartão de crédito parcelado sem necessidade é gasto futuro que seu eu de amanhã vai ter que pagar.");
  pool.push("Antes de comprar por impulso, espere 24 horas. Metade dessas vontades passa sozinha.");
  pool.push("Comparar preços antes de comprar algo caro pode economizar mais do que parece.");
  pool.push("Ter clareza dos gastos fixos (aluguel, contas, assinaturas) é a base de qualquer planejamento.");

  // --- Personalidade / leve ---
  pool.push("Tô de olho no seu extrato, viu? Brincadeira... ou não.");
  pool.push("Cofrinho que é cofrinho guarda moeda todo dia. Bora fazer sua parte?");
  pool.push("Psiu, vem cá: quem controla o dinheiro dorme bem melhor à noite.");
  pool.push("Fuçando aqui os seus números... quer ver o que eu achei?");
  pool.push("Eu não julgo seus gastos. Só reparo neles. Com bastante atenção.");

  return pool;
}

/**
 * Traduz os números reais do usuário num humor pro mascote e numa mensagem pra mostrar
 * quando ele é clicado. O humor vem do saldo/economia (como antes); a mensagem agora sai
 * de um baú com dezenas de frases — algumas com dados reais da conta (categoria que mais
 * pesa, maior gasto do mês, sequência de dias anotando, dia do mês que mais gastou...),
 * outras com dicas gerais de educação financeira, sorteada a cada clique.
 */
export function useFinancialMascot() {
  const { user } = useAuth();
  const isPremium = !!user?.isPremium;
  const { data: summary } = useDashboardSummary();
  const { data: period } = usePeriodSummary("month");
  const now = new Date();
  const { data: calendarThisMonth } = useCalendarSummary(now.getFullYear(), now.getMonth() + 1);
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const { data: calendarPrevMonth } = useCalendarSummary(prevDate.getFullYear(), prevDate.getMonth() + 1);

  const lastMessageRef = useRef<string | null>(null);

  const mood: MascotMood = useMemo(() => {
    if (!summary || !period) return "neutral";
    const savingsRate = period.income > 0 ? period.net / period.income : 0;
    if (summary.consolidatedBalance < 0) return "sad";
    if (savingsRate >= 0.2) return "ecstatic";
    if (savingsRate >= 0.05) return "happy";
    if (savingsRate >= -0.05) return "neutral";
    return "worried";
  }, [summary, period]);

  const basePool = useMemo(() => {
    if (!summary || !period) return [];
    const savingsRate = period.income > 0 ? period.net / period.income : 0;
    const facts: Facts = {
      consolidatedBalance: summary.consolidatedBalance,
      monthIncome: period.income,
      monthExpense: period.expense,
      monthNet: period.net,
      savingsRate,
      topCategory: summary.topCategory,
      secondCategory: summary.expensesByCategory[1] ?? null,
      categoryCount: summary.expensesByCategory.length,
      biggestExpense: summary.biggestExpense,
      streakDays: summary.streak.days,
      loggedToday: summary.streak.loggedToday,
      daysSinceLastLog: computeDaysSinceLastLog(calendarThisMonth?.days, calendarPrevMonth?.days),
      worstDay: computeWorstDay(calendarThisMonth?.days),
    };
    return buildMessagePool(facts);
  }, [summary, period, calendarThisMonth, calendarPrevMonth]);

  // Baú separado só com o pitch da assinatura — vazio pra quem já assina.
  const pitchPool = useMemo(() => (isPremium ? [] : PREMIUM_PITCH_MESSAGES), [isPremium]);

  const pickFrom = useCallback((source: string[]) => {
    if (source.length === 1) {
      lastMessageRef.current = source[0];
      return source[0];
    }
    let next = source[Math.floor(Math.random() * source.length)];
    // evita repetir a mesma frase duas vezes seguidas
    let guard = 0;
    while (next === lastMessageRef.current && guard < 6) {
      next = source[Math.floor(Math.random() * source.length)];
      guard++;
    }
    lastMessageRef.current = next;
    return next;
  }, []);

  const pickMessage = useCallback(() => {
    if (basePool.length === 0 && pitchPool.length === 0) return "Carregando suas finanças…";
    // Pra quem não assina: 50% das vezes o mascote puxa uma mensagem de assinatura, 50% uma
    // mensagem normal (insight/dica). Pra assinante, pitchPool é vazio e sempre cai no baú
    // normal.
    const usePitch = pitchPool.length > 0 && (basePool.length === 0 || Math.random() < 0.5);
    return pickFrom(usePitch ? pitchPool : basePool);
  }, [basePool, pitchPool, pickFrom]);

  return { mood, ready: basePool.length > 0 || pitchPool.length > 0, pickMessage };
}
